import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { I18nContext } from "../i18n/useI18n";

// Interface para MenuProps
interface MenuProps {
  links?: Array<{
    item: string;
    to?: string;
    links: Array<{ labelKey: string; to: string }>;
  }>;
  open?: boolean;
  onClick?: (open: boolean) => void;
}

const menuList = [
  {
    item: "project",
    links: [
      {
        labelKey: "description",
        to: "/description",
      },
      {
        labelKey: "engineering",
        to: "/engineering",
      },
      {
        labelKey: "modeling",
        to: "/modeling",
      },
      {
        labelKey: "parts",
        to: "/parts",
      },
      {
        labelKey: "contribution",
        to: "/contribution",
      },
    ],
  },
  {
    item: "experiments",
    links: [
      {
        labelKey: "measurement",
        to: "/measurement",
      },
      {
        labelKey: "notebook",
        to: "/notebook",
      },
      {
        labelKey: "safety",
        to: "/safety",
      },
      {
        labelKey: "protocols",
        to: "/protocols",
      },
    ],
  },
  {
    item: "publicEngagement",
    to: "/public-engagement",
    links: [
      {
        labelKey: "humanPractices",
        to: "/human-practices",
      },
      {
        labelKey: "entrepreneurship",
        to: "/entrepreneurship",
      },
      {
        labelKey: "education",
        to: "/education",
      },
      {
        labelKey: "implementation",
        to: "/implementation",
      },
    ],
  },
  {
    item: "team",
    links: [
      {
        labelKey: "members",
        to: "/members",
      },
      {
        labelKey: "attributions",
        to: "/attributions",
      },
    ],
  },

  {
    item: "awards",
    to: "/awards",
    links: [],
  },
];

const Menu = ({ links, open, onClick }: MenuProps) => {
  const { t } = useContext(I18nContext);
  const [openMenu, setOpenMenu] = useState<string>("");

  return (
    <ul
      className={clsx(
        "right-0 z-10 absolute bg-white shadow-lg",
        "md:relative md:top-auto md:right-auto md:shadow-none md:bg-transparent",
        "w-80 md:w-full",
        "menu menu-vertical md:menu-horizontal px-1 md:flex",
        "font-questrial text-lg md:text-xl lg:text-2xl",
        { hidden: !open }
      )}
    >
      {links?.map((link, index) => {
        return (
          <li key={`link-${index}`}>
            {link.links.length === 0 && link.to && (
              <Link to={link.to} onClick={() => onClick?.(false)}>
                {t(`header.link.${link.item}.title`)}
              </Link>
            )}
            {link.links.length > 0 && (
              <details
                open={openMenu === link.item}
                onToggle={(e) => {
                  if (e.currentTarget.open) {
                    setOpenMenu(link.item);
                  }
                }}
              >
                <summary>{t(`header.link.${link.item}.title`)}</summary>
                <ul className="right-0 min-w-40">
                  {link.links.map((sublink, subindex) => {
                    return (
                      <li key={`sublink-${sublink.labelKey}-${subindex}`}>
                        <Link
                          key={`sublink-${subindex}`}
                          to={sublink.to}
                          onClick={() => {
                            onClick?.(false);
                            setOpenMenu("");
                          }}
                        >
                          {t(`header.link.${link.item}.${sublink.labelKey}`)}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const Header = () => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  return (
    <nav className="top-0 z-10 sticky bg-gradient-to-r from-white to-primary shadow-lg text-secondary navbar">
      <div className="flex mx-auto px-3 lg:px-6 w-full max-w-7xl">
        <div className="flex-1">
          <Link to="/" className="flex h-full text-secondary text-xl">
            <img
              className="w-20 h-full"
              width={80}
              height={20}
              alt="Logo Yobe"
              src="https://static.igem.wiki/teams/5058/imgs/logo-banner.svg"
            />
          </Link>
        </div>
        <div className="flex-none">
          <div className="dropdown">
            <div
              tabIndex={0}
              role="button"
              className="flex justify-center items-center md:hidden text-secondary btn-circle"
              onClick={() => setOpenMenu(!openMenu)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h7"
                />
              </svg>
            </div>

            <Menu links={menuList} open={openMenu} onClick={setOpenMenu} />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
